import React, { useEffect, useState } from 'react';
import FacultyAssignmentPanel from './FacultyAssignmentPanel';
import api from '../../api';
import SubjectManager from './SubjectManager';
import SubjectModal from './SubjectModal';
import HODManager from './HODManager';
import ElectiveGroupManager from './ElectiveGroupManager';
import BulkUploadModal from './BulkUploadModal';

const DepartmentDetailModal = ({
  isOpen,
  onClose,
  department,
  onSaveDepartment,
  onCreateSubject,
  onEditSubject,
  onDeleteSubject,
  onAssignFacultyToSubject,
  onViewFacultyForSubject,
  loadingStates = {},
  userRole, // ✅ New prop to check user role
}) => {
  const [activeTab, setActiveTab] = useState('info');
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [groups, setGroups] = useState(() => Array.isArray(department?.groups) ? department.groups : []);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  const [subjectsState, setSubjectsState] = useState({});
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);

  const [stats, setStats] = useState({
    facultyCount: 0,
    groupsCount: 0,
    hodAssigned: false
  });

  // ✅ Check if user is tenant_admin
  const isTenantAdmin = userRole === 'tenant_admin';

  // Fetch profiles and stats upon expanding panel view
  useEffect(() => {
    if (!isOpen || !department?.tenantId || !department?._id) return;
    
    let cancelled = false;

    const loadAllStats = async () => {
      try {
        const facultyRes = await api.get(`/api/departments/${department.tenantId}/${department._id}/faculty`);
        const facultyData = Array.isArray(facultyRes?.data?.faculty) ? facultyRes.data.faculty : [];

        const subjectsRes = await api.get(`/api/subjects/get-subjects/${department._id}`, {
          params: { tenantId: department.tenantId }
        });
        const subjectsData = Array.isArray(subjectsRes?.data?.subjects) ? subjectsRes.data.subjects
          : Array.isArray(subjectsRes?.data?.data) ? subjectsRes.data.data
          : Array.isArray(subjectsRes?.data) ? subjectsRes.data : [];

        if (!cancelled) {
          setSubjectsState(prev => ({ ...prev, [department._id]: subjectsData }));
          setStats({
            facultyCount: facultyData.length,
            groupsCount: Array.isArray(department?.groups) ? department.groups.length : 0,
            hodAssigned: !!(department?.hodId || department?.hod?._id || department?.hod?.id)
          });
        }
      } catch (err) {
        console.error('Failed to load department summaries:', err?.response ?? err);
      }
    };

    loadAllStats();
    return () => { cancelled = true; };
  }, [isOpen, department]);

  useEffect(() => {
    setEditedName(department?.name || '');
    setIsEditing(false);
    setEditError(null);
    setSaving(false);
  }, [department, isOpen]);

  useEffect(() => {
    setGroups(Array.isArray(department?.groups) ? department.groups : []);
  }, [department]);

  const closeSubjectModal = () => {
    setEditingSubject(null);
    setShowSubjectModal(false);
  };

  const handleCreateSubject = async (subjectData) => {
    if (!department?._id || !department?.tenantId) {
      throw new Error('Department ID and Tenant ID are required');
    }

    const payload = {
      name: subjectData.name?.trim(),
      subjectCode: subjectData.subjectCode?.trim().toUpperCase(),
      departmentId: department._id,
      tenantId: department.tenantId,
      createdBy: getCurrentUserId(),
      subjectType: subjectData.subjectType || 'theory',
      customSubjectType: subjectData.subjectType === 'custom' ? subjectData.customSubjectType : '',
      subjectCategory: subjectData.subjectCategory || 'academic',
      requiresAttendance: subjectData.requiresAttendance !== false,
      appearsInTimetable: subjectData.appearsInTimetable !== false,
      isCreditBased: subjectData.isCreditBased || false,
      credits: subjectData.isCreditBased ? (subjectData.credits || 0) : 0,
      isLab: subjectData.subjectType === 'lab',
      isElective: subjectData.subjectType === 'elective',
      isLanguage: subjectData.subjectType === 'language',
      isProject: subjectData.subjectType === 'project',
      assessmentType: subjectData.assessmentType || 'marks',
      description: subjectData.description || '',
      maxMarks: Number(subjectData.maxMarks),
      cieMaxMarks: Number(subjectData.cieMaxMarks),
      endExamMaxMarks: Number(subjectData.endExamMaxMarks)
    };

    if (onCreateSubject) {
      return await onCreateSubject(payload);
    } else {
      try {
        const response = await api.post('/api/subjects/add-subject', payload);
        const newSubject = response.data.subject || response.data;
        
        if (department?._id) {
          setSubjectsState(prev => ({
            ...prev,
            [department._id]: [...(prev[department._id] || []), newSubject]
          }));
        }
        return newSubject;
      } catch (error) {
        console.error('Failed to create course:', error);
        throw error;
      }
    }
  };

  const handleEditSubject = async (subjectData) => {
    if (!subjectData.subjectId) {
      throw new Error('Subject ID is required for editing');
    }

    const payload = {
      name: subjectData.name?.trim(),
      subjectCode: subjectData.subjectCode?.trim().toUpperCase(),
      subjectType: subjectData.subjectType || 'theory',
      customSubjectType: subjectData.subjectType === 'custom' ? subjectData.customSubjectType : '',
      subjectCategory: subjectData.subjectCategory || 'academic',
      requiresAttendance: subjectData.requiresAttendance !== false,
      appearsInTimetable: subjectData.appearsInTimetable !== false,
      isCreditBased: subjectData.isCreditBased || false,
      credits: subjectData.isCreditBased ? (subjectData.credits || 0) : 0,
      isLab: subjectData.subjectType === 'lab',
      isElective: subjectData.subjectType === 'elective',
      isLanguage: subjectData.subjectType === 'language',
      isProject: subjectData.subjectType === 'project',
      assessmentType: subjectData.assessmentType || 'marks',
      description: subjectData.description || '',
      maxMarks: Number(subjectData.maxMarks),
      cieMaxMarks: Number(subjectData.cieMaxMarks),
      endExamMaxMarks: Number(subjectData.endExamMaxMarks)
    };

    if (onEditSubject) {
      return await onEditSubject({
        ...payload,
        subjectId: subjectData.subjectId,
        departmentId: department?._id,
        tenantId: department?.tenantId
      });
    } else {
      try {
        const response = await api.put(
          `/api/subjects/edit-subject/${subjectData.subjectId}`,
          { ...payload, departmentId: department?._id, tenantId: department?.tenantId }
        );
        const updatedSubject = response.data.subject || response.data;
        
        if (department?._id) {
          setSubjectsState(prev => ({
            ...prev,
            [department._id]: (prev[department._id] || []).map(subject => 
              subject._id === updatedSubject._id ? updatedSubject : subject
            )
          }));
        }
        return updatedSubject;
      } catch (error) {
        console.error('Failed to update course details:', error);
        throw error;
      }
    }
  };

  const handleDeleteSubject = async (subjectId) => {
    if (onDeleteSubject) {
      await onDeleteSubject(subjectId);
    } else {
      try {
        await api.delete(`/api/subjects/delete-subject/${subjectId}`, {
          data: { tenantId: department?.tenantId, departmentId: department?._id }
        });
        
        if (department?._id) {
          setSubjectsState(prev => ({
            ...prev,
            [department._id]: (prev[department._id] || []).filter(subject => subject._id !== subjectId)
          }));
        }
      } catch (error) {
        console.error('Failed to remove course:', error);
        throw error;
      }
    }
  };

  const handleSubjectSaved = (savedSubject) => {
    if (department?._id) {
      setSubjectsState(prev => {
        const currentSubjects = prev[department._id] || [];
        const existingIndex = currentSubjects.findIndex(s => s._id === savedSubject._id);
        const updatedSubjects = existingIndex >= 0 
          ? currentSubjects.map((subject, index) => index === existingIndex ? savedSubject : subject)
          : [...currentSubjects, savedSubject];
        return { ...prev, [department._id]: updatedSubjects };
      });
    }
    closeSubjectModal();
  };

  const getCurrentUserId = () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken');
      if (!token) return 'system';
      const payload = JSON.parse(window.atob(token.split('.')[1]));
      return payload.userId || payload.user_id || payload.sub || 'system';
    } catch (e) {
      return 'system';
    }
  };

  const handleStartEdit = () => {
    setEditedName(department.name);
    setIsEditing(true);
    setEditError(null);
  };

  const handleCancelEdit = () => {
    setEditedName(department.name);
    setIsEditing(false);
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    const trimmedName = editedName.trim();
    if (!trimmedName) {
      setEditError('Department name is required.');
      return;
    }
    if (trimmedName === department.name) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    setEditError(null);

    try {
      if (onSaveDepartment) {
        await onSaveDepartment({ name: trimmedName });
      }
      setIsEditing(false);
    } catch (err) {
      setEditError(err.message || 'Failed to update department name.');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignedChange = (assignedIds = []) => {
    const count = Array.isArray(assignedIds) ? assignedIds.length : 0;
    setStats(prev => ({ ...prev, facultyCount: count }));
    if (department) department.facultyIds = assignedIds;
  };

  const refreshDepartmentSubjects = async () => {
    if (!department?._id || !department?.tenantId) return;
    try {
      const subjectsRes = await api.get(`/api/subjects/get-subjects/${department._id}`, {
        params: { tenantId: department.tenantId }
      });
      const subjectsData = Array.isArray(subjectsRes?.data?.subjects) ? subjectsRes.data.subjects
        : Array.isArray(subjectsRes?.data?.data) ? subjectsRes.data.data
        : Array.isArray(subjectsRes?.data) ? subjectsRes.data : [];
      setSubjectsState(prev => ({ ...prev, [department._id]: subjectsData }));
    } catch (err) {
      console.error('Failed to refresh courses:', err);
    }
  };

  if (!isOpen || !department) return null;

  const formatTime = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // ✅ Define tabs based on user role
  const getAvailableTabs = () => {
    const tabs = [
      { id: 'info', label: 'Overview', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
      { id: 'faculty', label: 'Faculty Roster', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
      { id: 'subjects', label: 'Syllabus Catalog', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
      { id: 'groups', label: 'Elective Pools', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' }
    ];
    
    // ✅ Only show HOD tab for tenant_admin
    if (isTenantAdmin) {
      tabs.push({ id: 'hod', label: 'Head of Department', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' });
    }
    
    return tabs;
  };

  const tabs = getAvailableTabs();

  return (
    <>
      {/* Main Modal - Fixed positioning with highest z-index */}
      <div 
        className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/60 backdrop-blur-sm"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          overflow: 'hidden'
        }}
        onClick={(e) => {
          // Close modal when clicking backdrop
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div 
          className="bg-white w-full max-w-7xl h-[95vh] sm:h-[90vh] rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col transform transition-all animate-slideUp"
          style={{
            position: 'relative',
            zIndex: 10000,
            maxHeight: '95vh',
            margin: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          
          {/* Header Block */}
          <div className="px-4 py-4 sm:p-6 border-b border-slate-100 bg-slate-50/50 shrink-0">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white shrink-0 shadow-sm">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      className="text-base font-bold bg-white border border-slate-200 rounded-xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 w-full max-w-md text-slate-900"
                      autoFocus
                    />
                  ) : (
                    <h2 className="text-base sm:text-lg font-bold text-slate-900 truncate">{department.name}</h2>
                  )}
                  <p className="text-slate-500 text-xs mt-0.5 font-medium">Department Management Portal</p>
                </div>
              </div>

              {/* Header Interactive Layout */}
              <div className="flex items-center justify-end gap-2 shrink-0">
                {isEditing ? (
                  <div className="flex items-center gap-1.5 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={handleSaveEdit}
                      disabled={saving}
                      className="flex-1 sm:flex-none px-3.5 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors text-xs font-semibold flex items-center justify-center gap-1.5 shadow-sm"
                    >
                      {saving ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" /> : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      disabled={saving}
                      className="flex-1 sm:flex-none px-3.5 py-2 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors text-xs font-semibold"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleStartEdit}
                    className="px-3.5 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors text-xs font-semibold flex items-center gap-1.5"
                  >
                    <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Rename
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-xl transition-colors"
                  aria-label="Close panel"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Tab System Controls */}
          <div className="border-b border-slate-100 bg-white px-4 overflow-x-auto no-scrollbar shrink-0">
            <nav className="flex space-x-6 whitespace-nowrap">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`py-3 px-1 border-b-2 font-semibold text-xs sm:text-sm transition-all flex items-center gap-2 ${
                    activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                  </svg>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content Node Container */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50/50">
            
            {/* Overview Section Form Grid */}
            {activeTab === 'info' && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-slate-100 p-4 sm:p-6 shadow-sm space-y-6">
                  {editError && (
                    <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl flex items-center gap-2 text-rose-700 text-xs font-medium">
                      <svg className="w-4 h-4 shrink-0 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{editError}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Micro Analytical Cards Layout */}
                    <div className="lg:col-span-1 space-y-3">
                      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Department Summary</h3>
                      <div className="grid grid-cols-2 lg:grid-cols-1 gap-2.5">
                        {[
                          { label: 'Faculty Roster Strength', val: `${stats.facultyCount} Educators`, color: 'from-blue-500 to-indigo-600' },
                          { label: 'Elective Clusters', val: `${stats.groupsCount} Pools`, color: 'from-purple-500 to-pink-600' },
                          { label: 'Status', val: 'Active', color: 'from-slate-700 to-slate-900', isString: true }
                        ].map((item, idx) => (
                          <div key={idx} className="bg-white p-3.5 rounded-xl border border-slate-200/60 shadow-xs flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-500">{item.label}</span>
                            <span className={`text-sm lg:text-base font-bold bg-gradient-to-r ${item.color} bg-clip-text text-transparent`}>
                              {item.val}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Metadata fields descriptor */}
                    <div className="lg:col-span-2 space-y-4">
                      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Department Particulars</h3>
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-4">
                        <div>
                          <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1">Official Name</label>
                          <p className="text-slate-900 text-sm font-semibold">{department.name}</p>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-200/60">
                          <div>
                            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1">Created On</label>
                            <p className="text-slate-700 text-xs font-medium">{formatTime(department.createdAt)}</p>
                          </div>
                          {department.updatedAt && (
                            <div>
                              <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1">Last Updated</label>
                              <p className="text-slate-700 text-xs font-medium">{formatTime(department.updatedAt)}</p>
                            </div>
                          )}
                        </div>

                        {department.description && (
                          <div className="pt-2 border-t border-slate-200/60">
                            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1">Functional Description</label>
                            <p className="text-slate-600 text-xs leading-relaxed font-normal">{department.description}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Faculty Assignment Workspace Panel */}
            {activeTab === 'faculty' && (
              <div className="bg-white rounded-2xl border border-slate-100 p-4 sm:p-6 shadow-sm">
                <div className="mb-4">
                  <h3 className="text-sm font-bold text-slate-900">Personnel Allocation</h3>
                  <p className="text-slate-500 text-xs mt-0.5">Map lecturers and instructors directly to this academic department framework.</p>
                </div>
                <FacultyAssignmentPanel
                  tenantId={department?.tenantId}
                  department={department}
                  token={undefined}
                  onAssign={(ids) => {
                    department.facultyIds = ids;
                    handleAssignedChange(ids);
                  }}
                  onAssignedChange={handleAssignedChange}
                />
              </div>
            )}

            {/* Subjects Directory Submodule layout Context */}
            {activeTab === 'subjects' && (
              <div className="bg-white rounded-2xl border border-slate-100 p-4 sm:p-6 shadow-sm space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">Curriculum Registry</h3>
                    <p className="text-slate-500 text-xs mt-0.5">Manage syllabus courses and subject configurations under this department.</p>
                  </div>
                  <button
                    onClick={() => setShowBulkUploadModal(true)}
                    className="w-full sm:w-auto px-4 py-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors text-xs font-semibold flex items-center justify-center gap-2"
                    type="button"
                  >
                    <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Bulk Import Courses (.csv)
                  </button>
                </div>

                <SubjectManager
                  department={department}
                  subjects={subjectsState}
                  onCreateSubject={handleCreateSubject}
                  onEditSubject={handleEditSubject}
                  onDeleteSubject={handleDeleteSubject}
                  onSubjectsChange={(newSubjects) => {
                    if (department?._id) {
                      setSubjectsState(prev => ({ ...prev, [department._id]: newSubjects }));
                    }
                    if (department && onSaveDepartment) {
                      onSaveDepartment({
                        ...department,
                        subjects: Array.isArray(newSubjects) ? newSubjects : department.subjects
                      });
                    }
                  }}
                  onAssignFacultyToSubject={onAssignFacultyToSubject}
                  onViewFacultyForSubject={onViewFacultyForSubject}
                  loadingStates={loadingStates}
                />

                {showSubjectModal && (
                  <SubjectModal
                    isOpen={showSubjectModal}
                    onClose={closeSubjectModal}
                    title={editingSubject ? 'Edit Course Details' : 'Create New Syllabus Course'}
                    subject={editingSubject}
                    onSubmit={async (subjectData) => {
                      let result = editingSubject 
                        ? await handleEditSubject({ ...subjectData, subjectId: editingSubject._id })
                        : await handleCreateSubject(subjectData);
                      handleSubjectSaved(result);
                    }}
                    loading={loadingStates['create-subject'] || loadingStates['edit-subject']}
                    actionText={editingSubject ? 'Save Changes' : 'Create Course'}
                    editMode={!!editingSubject}
                    tenantId={department?.tenantId}
                    departmentId={department?._id}
                  />
                )}
              </div>
            )}

            {/* Cluster elective grouping panel context modules */}
            {activeTab === 'groups' && (
              <div className="bg-white rounded-2xl border border-slate-100 p-4 sm:p-6 shadow-sm">
                <ElectiveGroupManager
                  tenantId={department?.tenantId}
                  departmentId={department?._id}
                  department={department}
                  token={localStorage.getItem('token')}
                  groups={groups}
                  onCreateGroup={(group) => {
                    setGroups(prev => [...prev, group]);
                    setStats(prev => ({ ...prev, groupsCount: prev.groupsCount + 1 }));
                  }}
                  onUpdateGroup={(updated) => {
                    setGroups(prev => prev.map(g => (g.id === updated.id ? updated : g)));
                  }}
                  onDeleteGroup={(id) => {
                    setGroups(prev => prev.filter(g => g.id !== id));
                    setStats(prev => ({ ...prev, groupsCount: Math.max(0, prev.groupsCount - 1) }));
                  }}
                />
              </div>
            )}

            {/* ✅ Higher Administration / HOD Framework Assignment Controls - Only shown for tenant_admin */}
            {activeTab === 'hod' && isTenantAdmin && (
              <div className="bg-white rounded-2xl border border-slate-100 p-4 sm:p-6 shadow-sm">
                <div className="mb-4">
                  <h3 className="text-sm font-bold text-slate-900">Head of Department Appointment</h3>
                  <p className="text-slate-500 text-xs mt-0.5">Assign leadership and administrative control privileges to a specific educator profile.</p>
                </div>
                <HODManager
                  tenantId={department?.tenantId}
                  departmentId={department?._id}
                  departmentName={department?.name}
                  onHODChanged={async (hodId) => {
                    if (typeof onSaveDepartment === 'function') {
                      await onSaveDepartment({ hodId });
                    } else if (department) {
                      department.hodId = hodId;
                      if (department.hod) delete department.hod;
                    }
                    setStats(prev => ({ ...prev, hodAssigned: !!hodId }));
                  }}
                />
              </div>
            )}

            {/* ✅ Show message for non-tenant_admin trying to access HOD tab */}
            {activeTab === 'hod' && !isTenantAdmin && (
              <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-sm font-bold text-slate-900 mb-1">Access Restricted</h3>
                <p className="text-sm text-slate-500 max-w-sm mx-auto">
                  Head of Department management is only available to Tenant Administrators.
                </p>
              </div>
            )}
          </div>

          {/* Modal File System Upload Overlays */}
          {showBulkUploadModal && (
            <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 99999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(15, 23, 42, 0.6)',
                backdropFilter: 'blur(8px)',
                animation: 'fadeIn 0.2s ease-out'
              }}
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowBulkUploadModal(false);
                }
              }}
            >
              <div 
                style={{
                  position: 'relative',
                  zIndex: 100000,
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  margin: 'auto'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <BulkUploadModal
                  isOpen={showBulkUploadModal}
                  onClose={() => setShowBulkUploadModal(false)}
                  tenantId={department?.tenantId}
                  department={department}
                  createdBy={getCurrentUserId()}
                  refreshData={refreshDepartmentSubjects}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Global CSS for animations */}
      <style jsx>{`
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
        
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </>
  );
};

export default DepartmentDetailModal;